import { useEffect, useRef, useState } from 'react'
import {
    ScoopText,
    ScoopInfoTooltip,
    ScoopButton,
    ScoopMenu,
    ScoopMenuItem,
    ScoopCheckbox,
    ScoopTree,
    ScoopDivider,
    ScoopSkeleton,
    ScoopRow,
    ScoopColumn,
} from '../../../../ui'
import {
    CaretRightIcon,
    PlusIcon,
    FolderIcon,
    DotsThreeIcon,
    CheckIcon,
    PencilSimpleIcon,
    CopyIcon,
    TrashIcon,
} from '@phosphor-icons/react'
import { WorksheetMetric } from '../../../../Insights/WorksheetMetric'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { cloneDeep } from 'lodash'
import styles from './DataTab.module.scss'
import { useModal, MODALS } from '../../../../modals/'

export const DataTab = ({
    server,
    config,
    setConfig,
    chartState,
    getWorkspaceMetadata,
    workspaceID,
    worksheetMetrics,
    setWorksheetMetrics,
}) => {
    const [newItems, setNewItems] = useState(config.selectedItems)
    const [selectedMetric, setSelectedMetric] = useState(null)
    const [itemOptionsMenuOpen, setItemOptionsMenuOpen] = useState(false)
    const [createKpiMenuOpen, setCreateKpiMenuOpen] = useState(false)
    const [currentItem, setCurrentItem] = useState(null)
    const [expanded, setExpanded] = useState([])
    const [deleteKpi, setDeleteKpi] = useState(false)
    const [derived, setDerived] = useState(false)

    // Modal Manager hooks
    const newMetricModal = useModal(MODALS.EXPLORER.NEW_METRIC)
    const sheetSourceModal = useModal(MODALS.EXPLORER.SHEET_SOURCE)
    const [kpisCollapsed, setKpisCollapsed] = useState(false)
    const [sourcesCollapsed, setSourcesCollapsed] = useState(false)
    const [worksheetsCollapsed, setWorksheetsCollapsed] = useState(false)
    const [kpis, setKpis] = useState([])
    const [searchFilter, setSearchFilter] = useState('')
    const [kpiMenuAnchorEl, setKpiMenuAnchorEl] = useState(null)
    const [dateMenuAnchorEl, setDateMenuAnchorEl] = useState(null)
    const [dateMenuColumn, setDateMenuColumn] = useState(null)
    const [kpiOptionsMenuAnchorEl, setKpiOptionsMenuAnchorEl] = useState(null)
    const [kpiOptionsMenuKpi, setKpiOptionsMenuKpi] = useState(null)
    const currentItemRef = useRef()
    const { workspaceMetadata, isLoading } = useWorkspaceMetadata()

    useEffect(() => {
        if (config.selectedItems !== newItems) {
            const newConfig = cloneDeep(config)
            newConfig.selectedItems = newItems
            newConfig.selectedItems.forEach((item) => {
                if (
                    item.columnName &&
                    !newConfig.selectedDates.has(item.reportSeriesTableID + ':' + item.columnName)
                ) {
                    const dates = workspaceMetadata?.inboxes
                        ?.find((i) =>
                            i.tables.some((t) => t.reportSeriesTableID === item.reportSeriesTableID)
                        )
                        ?.tables?.find(
                            (t) => t.reportSeriesTableID === item.reportSeriesTableID
                        )?.dates
                    if (dates?.length === 1) {
                        newConfig.selectedDates.set(
                            item.reportSeriesTableID + ':' + item.columnName,
                            dates[0]
                        )
                    }
                }
            })
            if (newConfig.selectedItems.length > 0) {
                chartState.getResults(newConfig)
                setConfig(newConfig)
            } else {
                chartState.clear()
            }
        }
    }, [newItems])

    useEffect(() => {
        if (config.allowedKPIs) {
            setKpis(
                workspaceMetadata?.kpis?.filter((kpi) =>
                    config.allowedKPIs.includes(kpi.metricName)
                ) || []
            )
        } else {
            setKpis(workspaceMetadata?.kpis || [])
        }
    }, [config.allowedKPIs, workspaceMetadata])

    useEffect(() => {
        if (config.selectedItems.length === 0 && newItems.length !== 0) setNewItems([])
    }, [config.selectedItems])

    const setSelectedColumn = (event) => {
        if (!event.target.value) return
        let { reportSeriesTableID, columnName, kpi } = JSON.parse(event.target.value)
        let found = false
        if (config.view === 'kpi') {
            if (kpi) {
                if (
                    newItems.some(
                        (i) => i.reportSeriesTableID === reportSeriesTableID && i.kpi === kpi
                    )
                ) {
                    setNewItems([])
                } else {
                    setNewItems((state) => [{ reportSeriesTableID: reportSeriesTableID, kpi: kpi }])
                }
            } else {
                if (
                    newItems.some(
                        (i) =>
                            i.reportSeriesTableID === reportSeriesTableID &&
                            i.columnName === columnName
                    )
                ) {
                    setNewItems([])
                } else {
                    setNewItems((state) => [
                        {
                            reportSeriesTableID: reportSeriesTableID,
                            columnName: columnName,
                        },
                    ])
                }
            }
        }
        if (config.view === 'table') {
            const newConfig = { ...config }
            if (newConfig.selectedTableKpis.some((k) => k.metricName === kpi)) {
                // Deleting
                const deleteIndex = newConfig.selectedTableKpis.findIndex(
                    (k) => k.metricName === kpi
                )
                newConfig.selectedTableKpis.splice(deleteIndex, 1)
                if (
                    newConfig.selectedTables.includes(reportSeriesTableID) &&
                    !newConfig.selectedTableKpis.some(
                        (k) => k.reportSeriesTableID === reportSeriesTableID
                    )
                ) {
                    newConfig.selectedTables.splice(
                        newConfig.selectedTables.indexOf(reportSeriesTableID),
                        1
                    )
                }
                setConfig(newConfig)
            } else {
                // Adding
                workspaceMetadata.kpis.forEach((k) => {
                    if (k.metricName === kpi) newConfig.selectedTableKpis.push(k)
                })
                if (!newConfig.selectedTables.includes(reportSeriesTableID)) {
                    newConfig.selectedTables.push(reportSeriesTableID)
                }
                setConfig(newConfig)
            }
        }
        if (config.view === 'chart') {
            if (kpi) {
                for (var i = 0; i < newItems.length; i++) {
                    if (
                        newItems[i].reportSeriesTableID === reportSeriesTableID &&
                        newItems[i].kpi === kpi
                    ) {
                        found = true
                        const temp = [...newItems]
                        temp.splice(i, 1)
                        setNewItems([...temp])
                    }
                }
                if (!found) {
                    if (
                        !(
                            (config.seriesType === 'pie' || config.seriesType === 'donut') &&
                            config.selectedItems.length === 1
                        )
                    ) {
                        setNewItems((state) => [
                            ...state,
                            {
                                reportSeriesTableID: reportSeriesTableID,
                                kpi: kpi,
                            },
                        ])
                        if (newItems?.length === 1 && workspaceMetadata) {
                            for (i = 0; i < workspaceMetadata.kpis?.length; i++) {
                                if (workspaceMetadata.kpis[i].metricName === kpi) {
                                    config.period = workspaceMetadata.kpis[i].period
                                    setConfig({ ...config })
                                    break
                                }
                            }
                        }
                    }
                }
            } else {
                for (i = 0; i < newItems.length; i++) {
                    if (
                        newItems[i].reportSeriesTableID === reportSeriesTableID &&
                        newItems[i].columnName === columnName
                    ) {
                        found = true
                        const temp = [...newItems]
                        temp.splice(i, 1)
                        setNewItems([...temp])
                    }
                }
                if (!found) {
                    if (
                        !(
                            (config.seriesType === 'pie' || config.seriesType === 'donut') &&
                            config.selectedItems.length === 1
                        )
                    ) {
                        setNewItems((state) => [
                            ...state,
                            {
                                reportSeriesTableID: reportSeriesTableID,
                                columnName: columnName,
                            },
                        ])
                    }
                }
            }
        }
    }

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds)
    }

    const setSelectedColumnDate = (value) => {
        let { selectedDate, reportSeriesTableID, columnName } = JSON.parse(value)
        const newConfig = cloneDeep(config)
        if (selectedDate === 'Snapshot Date') {
            newConfig.selectedDates.delete(reportSeriesTableID + ':' + columnName)
        } else {
            newConfig.selectedDates.set(reportSeriesTableID + ':' + columnName, selectedDate)
        }
        chartState.getResults(newConfig)
        setConfig(newConfig)
    }

    const hasMeasures = (table) => {
        for (var i = 0; i < table.columns?.length; i++) {
            if (
                config.drillAttribute &&
                chartState.allowedReportSeriesTables &&
                chartState.allowedReportSeriesTables.indexOf(table.reportSeriesTableID) < 0
            )
                return false
            if (table.columns[i].isMeasure) return true
        }
        return false
    }

    const hasTablesWithMeasures = (inbox) => {
        for (var i = 0; i < inbox.tables?.length; i++) {
            if (hasMeasures(inbox.tables[i])) return true
        }
        return false
    }

    const deleteItem = () => {
        setItemOptionsMenuOpen(false)
        const newConfig = { ...config }
        let index
        if (currentItem.columnName) {
            index = newConfig.selectedItems.findIndex(
                (i) =>
                    i.reportSeriesTableID === currentItem.reportSeriesTableID &&
                    i.columnName === currentItem.columnName
            )
        } else {
            index = newConfig.selectedItems.findIndex(
                (i) =>
                    i.reportSeriesTableID === currentItem.reportSeriesTableID &&
                    i.kpi === currentItem.kpi
            )
        }
        newConfig.selectedItems.splice(index, 1)
        chartState.getResults(newConfig)
        setConfig(newConfig)
        setCurrentItem(null)
    }

    const editItem = () => {
        if (currentItem.kpi) {
            workspaceMetadata.kpis?.forEach((kpi) => {
                if (
                    kpi.metricName === currentItem.kpi &&
                    kpi.reportSeriesTableID === currentItem.reportSeriesTableID
                )
                    setSelectedMetric(kpi)
            })
        } else {
            // fin better solution for this, setting selected metric without searching all tables
            workspaceMetadata.inboxes?.forEach((inbox) => {
                inbox.tables?.forEach((table) => {
                    table.columns?.forEach((col) => {
                        if (
                            col.columnName === currentItem.columnName &&
                            table.reportSeriesTableID === currentItem.reportSeriesTableID
                        )
                            setSelectedMetric(col)
                    })
                })
            })
        }
    }

    const handleCreateKpi = (isDerived) => {
        setDerived(isDerived)
        newMetricModal.show({
            config,
            setConfig,
            chartState,
            derived: isDerived,
            editMetric: null,
            deleteMetric: false,
            kpis: getAvailableKpis(),
        })
        setCreateKpiMenuOpen(false)
    }

    const handleTableSelection = (table) => {
        const newConfig = { ...config }
        const tableId = table.reportSeriesTableID
        if (!newConfig.selectedTables.includes(tableId)) newConfig.selectedTables.push(tableId)
        else newConfig.selectedTables.splice(newConfig.selectedTables.indexOf(tableId), 1)
        setConfig(newConfig)
    }

    const getAvailableTables = (inbox) => {
        if (config.allowedReportSeriesTables) {
            return (
                inbox?.tables?.filter((table) =>
                    config.allowedReportSeriesTables.includes(table.reportSeriesTableID)
                ) || []
            )
        } else {
            return inbox?.tables || []
        }
    }

    const handleWorksheetMetricSelect = (col) => {
        const newConfig = { ...config }
        if (newConfig.selectedItems.some((it) => it.measureName === col.columnName)) {
            const deleteIndex = newConfig.selectedItems.findIndex(
                (it) => it.measureName === col.columnName
            )
            newConfig.selectedItems.splice(deleteIndex, 1)
            setConfig(newConfig)
            if (newConfig.selectedItems.length > 0) chartState.getResults(newConfig)
        } else {
            newConfig.selectedItems.push({
                measureName: col.columnName,
                worksheetID: config.worksheetID,
                rangeName: config.rangeName,
            })
            if (config.worksheetColumns.some((c) => c.columnType === 'DateTime')) {
                const dateCol = config.worksheetColumns.find((c) => c.columnType === 'DateTime')
                newConfig.selectedDates.set(
                    config.worksheetID + ':' + col.columnName,
                    dateCol.columnName
                )
            }
            setConfig(newConfig)
            chartState.getResults(newConfig)
        }
    }

    const handleUnselectDeletedMetric = (metric) => {
        const newConfig = { ...config }
        if (newConfig.view === 'chart') {
            if (newConfig.selectedItems.some((it) => it.kpi === metric.metricName)) {
                const deleteIndex = newConfig.selectedItems.findIndex(
                    (it) => it.kpi === metric.metricName
                )
                newConfig.selectedItems.splice(deleteIndex, 1)
                setConfig(newConfig)
                if (newConfig.selectedItems.length > 0) chartState.getResults(newConfig)
            }
        } else {
            if (newConfig.selectedTableKpis.some((kpi) => kpi.metricName === metric.metricName)) {
                const deleteIndex = newConfig.selectedTableKpis.findIndex(
                    (it) => it.metricName === metric.metricName
                )
                newConfig.selectedTableKpis.splice(deleteIndex, 1)
                setConfig(newConfig)
            }
        }
    }

    const handleOpenMetric = (metric) => {
        setSelectedMetric(metric)
        setDerived(metric.derived)
        newMetricModal.show({
            config,
            setConfig,
            chartState,
            derived: metric.derived,
            editMetric: metric,
            deleteMetric: false,
            kpis: getAvailableKpis(),
        })
    }

    const getKpiName = (metricName) => {
        const kpiNames = workspaceMetadata?.kpis?.map((kpi) => kpi.metricName) || []
        let i = 2
        while (kpiNames.includes(metricName + ` (${i})`)) i++
        return metricName + ` (${i})`
    }

    const handleCloneKpi = (kpi) => {
        let newKpi = cloneDeep(kpi)
        delete newKpi.metricKey
        newKpi.metricName = getKpiName(newKpi.metricName)
        server.postData(
            {
                action: 'putObject',
                class: 'scoop.metric.Metric',
                value: newKpi,
            },
            () => {
                setKpiOptionsMenuAnchorEl(null)
                setKpiOptionsMenuKpi(null)
                getWorkspaceMetadata()
            }
        )
    }

    const handleDeleteKpi = (kpi) => {
        setSelectedMetric({ ...kpi })
        setDeleteKpi(true)
        setKpiOptionsMenuAnchorEl(null)
        setKpiOptionsMenuKpi(null)
    }

    const handleEditKpi = (kpi) => {
        handleOpenMetric(kpi)
        setKpiOptionsMenuAnchorEl(null)
        setKpiOptionsMenuKpi(null)
    }

    const getAvailableKpis = () => {
        return kpis.filter(
            (k) =>
                k.metricName.toLowerCase().includes(searchFilter.toLowerCase().trim()) &&
                (!config.drillAttribute ||
                    !chartState.allowedKPIs ||
                    chartState.allowedKPIs.indexOf(k.metricName) >= 0)
        )
    }

    const getSectionHeight = (section) => {
        switch (section) {
            case 'kpis':
                if (kpisCollapsed) {
                    return 50
                } else {
                    if (config.worksheetID && config.view !== 'table') {
                        if (sourcesCollapsed && worksheetsCollapsed) return 'calc(100% - 160px)'
                        if (sourcesCollapsed || worksheetsCollapsed) return 'calc(50% - 50px)'
                        return '30%'
                    } else {
                        if (sourcesCollapsed) return 'calc(100% - 150px)'
                        return 'calc(50% - 50px)'
                    }
                }
            case 'sources':
                if (sourcesCollapsed) {
                    return 50
                } else {
                    if (config.worksheetID && config.view !== 'table') {
                        if (kpisCollapsed && worksheetsCollapsed) return 'calc(100% - 50px)'
                        if (kpisCollapsed || worksheetsCollapsed) return 'calc(50% - 50px)'
                        return '30%'
                    } else {
                        if (kpisCollapsed) return 'calc(100% - 150px)'
                        return 'calc(50% - 50px)'
                    }
                }
            case 'worksheet':
                if (worksheetsCollapsed) {
                    return 40
                } else {
                    if (kpisCollapsed && sourcesCollapsed) return 'calc(100% - 50px)'
                    if (kpisCollapsed || sourcesCollapsed) return 'calc(50% - 50px)'
                    return '30%'
                }
        }
    }

    function buildKpiTree(kpis) {
        const tree = {}
        tree['Default'] = []
        kpis.forEach((kpi) => {
            const parts = (kpi.path || '').split('/').filter(Boolean)

            let node = tree
            if (parts.length > 0) {
                for (const part of parts) {
                    if (!node[part]) node[part] = []
                    node = node[part]
                }
            } else {
                node = tree['Default']
            }
            if (!Array.isArray(node)) {
                console.error('Expected node to be an array, but it is not:', node)
            } else {
                node.push(kpi)
            }
        })

        return tree
    }

    // Transform KPI tree to ScoopTree format
    const transformKpiTreeToTreeData = (tree, path = '') => {
        return Object.entries(tree).flatMap(([key, value]) => {
            const nodePath = `${path}/${key}`

            // If value is an array, it's either a folder with KPIs or just KPIs
            if (Array.isArray(value)) {
                // Folder with KPIs
                return {
                    id: nodePath,
                    label: key,
                    isFolder: true,
                    children: value.map((kpi) => ({
                        id: kpi.metricKey,
                        label: kpi.metricName,
                        kpiData: kpi, // Keep original KPI data
                    })),
                }
            } else if (typeof value === 'object') {
                // Nested folders
                return {
                    id: nodePath,
                    label: key,
                    isFolder: true,
                    children: transformKpiTreeToTreeData(value, nodePath),
                }
            }
            return []
        })
    }

    // Transform inboxes data to ScoopTree format
    const transformInboxesToTreeData = () => {
        if (!workspaceMetadata?.inboxes) return []

        return workspaceMetadata.inboxes
            .filter((inbox) => hasTablesWithMeasures(inbox))
            .map((inbox) => ({
                id: inbox.inboxName,
                label: inbox.label,
                tooltip: inbox.label,
                children: getAvailableTables(inbox)
                    .filter((table) => hasMeasures(table))
                    .map((table) => ({
                        id: table.reportSeriesTableID,
                        label: table.tableName,
                        tableData: table, // Keep original table data
                        children:
                            config.view !== 'table'
                                ? table.columns
                                      ?.filter((col) => col.isMeasure)
                                      .map((column) => ({
                                          id: `${table.reportSeriesTableID}:${column.columnName}`,
                                          label: column.columnName,
                                          columnData: column, // Keep original column data
                                          tableData: table,
                                      }))
                                : undefined,
                    })),
            }))
    }

    return (
        <>
            <div className={styles.section} style={{ height: getSectionHeight('kpis') }}>
                <ScoopRow className={styles.sectionHeader}>
                    <ScoopRow className={styles.sectionHeaderLeft}>
                        <ScoopRow
                            className={styles.sectionHeaderTitle}
                            onClick={() => setKpisCollapsed(!kpisCollapsed)}
                        >
                            <CaretRightIcon
                                size={18}
                                className={`${styles.collapseIcon} ${
                                    kpisCollapsed
                                        ? styles.collapseIconCollapsed
                                        : styles.collapseIconExpanded
                                }`}
                            />
                            <ScoopText className={styles.sectionHeaderTitleText}>KPIs</ScoopText>
                        </ScoopRow>
                        <ScoopInfoTooltip
                            placement="right"
                            title={
                                'Key Performance Indicators (KPIs) allow you to aggregate data precisely, ' +
                                'create custom metrics, and build complex calculations across datasets.'
                            }
                        />
                    </ScoopRow>
                    <ScoopButton
                        variant="text"
                        size="xxs"
                        className={styles.addButton}
                        onClick={(e) => setKpiMenuAnchorEl(e.currentTarget)}
                    >
                        <PlusIcon size={16} weight="bold" />
                    </ScoopButton>
                </ScoopRow>
                {isLoading ? (
                    <ScoopColumn className={styles.skeletonContainer}>
                        {Array.from({ length: 6 }, (_, i) => (
                            <ScoopSkeleton variant="rounded" key={i} width="100%" height={25} />
                        ))}
                    </ScoopColumn>
                ) : workspaceMetadata && kpis && kpis.length > 0 ? (
                    <ScoopTree
                        data={transformKpiTreeToTreeData(buildKpiTree(getAvailableKpis()))}
                        selectable={false}
                        searchable={true}
                        searchPlaceholder="Search KPIs..."
                        searchSize="xs"
                        searchPadding="5px"
                        searchWidth="250px"
                        searchHeight="30px"
                        className={styles.kpiTree}
                        onNodeClick={(node) => {
                            if (!node.kpiData) return
                            const syntheticEvent = {
                                target: {
                                    value: JSON.stringify({
                                        reportSeriesTableID: node.kpiData.reportSeriesTableID,
                                        columnName: undefined,
                                        kpi: node.kpiData.metricName,
                                    }),
                                },
                            }
                            setSelectedColumn(syntheticEvent)
                        }}
                        renderIcon={(node, level, isExpanded) => {
                            if (level === 0 && node.isFolder) {
                                return (
                                    <FolderIcon
                                        size={16}
                                        weight={isExpanded ? 'fill' : 'regular'}
                                    />
                                )
                            }
                            return null
                        }}
                        renderNode={(node) => {
                            if (!node.kpiData) {
                                return (
                                    <ScoopText className={styles.folderLabel}>
                                        {node.label}
                                    </ScoopText>
                                )
                            }

                            const isSelected = config.selectedItems.some(
                                (item) =>
                                    item.reportSeriesTableID === node.kpiData.reportSeriesTableID &&
                                    item.kpi === node.kpiData.metricName
                            )

                            const handleSelect = () => {
                                const syntheticEvent = {
                                    target: {
                                        value: JSON.stringify({
                                            reportSeriesTableID: node.kpiData.reportSeriesTableID,
                                            columnName: undefined,
                                            kpi: node.kpiData.metricName,
                                        }),
                                    },
                                }
                                setSelectedColumn(syntheticEvent)
                            }

                            return (
                                <ScoopRow className={styles.kpiRow}>
                                    <ScoopRow onClick={(e) => e.stopPropagation()}>
                                        <ScoopCheckbox
                                            checked={isSelected}
                                            onChange={handleSelect}
                                            size="sm"
                                        />
                                    </ScoopRow>
                                    <ScoopText className={styles.kpiLabel}>{node.label}</ScoopText>
                                    <ScoopButton
                                        variant="icon"
                                        size="xxs"
                                        className={styles.kpiOptionsButton}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setKpiOptionsMenuAnchorEl(e.currentTarget)
                                            setKpiOptionsMenuKpi(node.kpiData)
                                        }}
                                    >
                                        <DotsThreeIcon size={16} weight="bold" />
                                    </ScoopButton>
                                </ScoopRow>
                            )
                        }}
                    />
                ) : (
                    <ScoopColumn className={styles.emptyState}>
                        <ScoopText className={styles.emptyStateText}>No KPIs available</ScoopText>
                        <ScoopText className={styles.emptyStateSubtext}>
                            Click the + button to create a KPI
                        </ScoopText>
                    </ScoopColumn>
                )}
            </div>
            <ScoopDivider />
            <div className={styles.section} style={{ height: getSectionHeight('sources') }}>
                <ScoopRow className={styles.sectionHeader}>
                    <ScoopRow className={styles.sectionHeaderLeft}>
                        <ScoopRow
                            className={styles.sectionHeaderTitle}
                            onClick={() => setSourcesCollapsed(!sourcesCollapsed)}
                        >
                            <CaretRightIcon
                                size={18}
                                className={`${styles.collapseIcon} ${
                                    sourcesCollapsed
                                        ? styles.collapseIconCollapsed
                                        : styles.collapseIconExpanded
                                }`}
                            />
                            <ScoopText className={styles.sectionHeaderTitleText}>
                                {config.view !== 'table' ? 'Source Metrics' : 'Data Sources'}
                            </ScoopText>
                        </ScoopRow>
                    </ScoopRow>
                </ScoopRow>
                {isLoading ? (
                    <ScoopColumn className={styles.skeletonContainer}>
                        {Array.from({ length: 6 }, (_, i) => (
                            <ScoopSkeleton variant="rounded" key={i} width="100%" height={25} />
                        ))}
                    </ScoopColumn>
                ) : transformInboxesToTreeData().length > 0 ? (
                    <ScoopTree
                        data={transformInboxesToTreeData()}
                        selectable={false}
                        searchable={true}
                        searchPlaceholder={
                            config.view !== 'table'
                                ? 'Search Source Metrics...'
                                : 'Search Data Sources...'
                        }
                        searchSize="xs"
                        searchPadding="5px"
                        searchWidth="250px"
                        searchHeight="30px"
                        className={styles.sourceMetricsTree}
                        onNodeClick={(node) => {
                            // Handle table selection (Table view)
                            if (config.view === 'table' && node.tableData && !node.columnData) {
                                handleTableSelection(node.tableData)
                                return
                            }
                            // Handle column selection (Chart/KPI view)
                            if (node.columnData) {
                                const syntheticEvent = {
                                    target: {
                                        value: JSON.stringify({
                                            reportSeriesTableID: node.tableData.reportSeriesTableID,
                                            columnName: node.columnData.columnName,
                                            kpi: undefined,
                                        }),
                                    },
                                }
                                setSelectedColumn(syntheticEvent)
                            }
                        }}
                        renderNode={(node) => {
                            // Table view - show checkbox for tables
                            if (config.view === 'table' && node.tableData && !node.columnData) {
                                const handleTableSelect = () => handleTableSelection(node.tableData)
                                return (
                                    <ScoopRow className={styles.tableSelectionRow}>
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <ScoopCheckbox
                                                checked={config.selectedTables.includes(
                                                    node.tableData.reportSeriesTableID
                                                )}
                                                onChange={handleTableSelect}
                                                size="medium"
                                            />
                                        </div>
                                        <ScoopText className={styles.tableLabel}>
                                            {node.label}
                                        </ScoopText>
                                    </ScoopRow>
                                )
                            }

                            // Chart/KPI view - columns with checkbox
                            if (node.columnData) {
                                const isSelected = config.selectedItems.some(
                                    (item) =>
                                        item.reportSeriesTableID ===
                                            node.tableData.reportSeriesTableID &&
                                        item.columnName === node.columnData.columnName
                                )
                                const hasDates = isSelected && node.tableData?.dates?.length > 0

                                const handleColumnSelect = () => {
                                    const syntheticEvent = {
                                        target: {
                                            value: JSON.stringify({
                                                reportSeriesTableID:
                                                    node.tableData.reportSeriesTableID,
                                                columnName: node.columnData.columnName,
                                                kpi: undefined,
                                            }),
                                        },
                                    }
                                    setSelectedColumn(syntheticEvent)
                                }

                                return (
                                    <ScoopRow className={styles.columnRow}>
                                        <ScoopRow onClick={(e) => e.stopPropagation()}>
                                            <ScoopCheckbox
                                                checked={isSelected}
                                                onChange={handleColumnSelect}
                                                size="sm"
                                            />
                                        </ScoopRow>
                                        <ScoopText className={styles.columnLabel}>
                                            {node.label}
                                        </ScoopText>
                                        {hasDates && (
                                            <ScoopButton
                                                variant="icon"
                                                size="xxs"
                                                className={styles.dateMenuButton}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setDateMenuAnchorEl(e.currentTarget)
                                                    setDateMenuColumn({
                                                        tableData: node.tableData,
                                                        columnData: node.columnData,
                                                    })
                                                }}
                                            >
                                                <DotsThreeIcon size={16} weight="bold" />
                                            </ScoopButton>
                                        )}
                                    </ScoopRow>
                                )
                            }

                            // Default label for inbox nodes
                            return (
                                <ScoopText className={styles.treeNodeLabel}>{node.label}</ScoopText>
                            )
                        }}
                    />
                ) : (
                    <ScoopColumn className={styles.emptyState}>
                        <ScoopText className={styles.emptyStateText}>
                            No data sources available
                        </ScoopText>
                        <ScoopText className={styles.emptyStateSubtext}>
                            Connect a data source to get started
                        </ScoopText>
                    </ScoopColumn>
                )}
            </div>
            <ScoopDivider />
            {config.worksheetID && config.view !== 'table' && (
                <>
                    <div
                        className={styles.section}
                        style={{ height: getSectionHeight('worksheet') }}
                    >
                        <ScoopRow className={styles.sectionHeader}>
                            <ScoopRow
                                className={styles.sectionHeaderTitle}
                                onClick={() => setWorksheetsCollapsed(!worksheetsCollapsed)}
                            >
                                <CaretRightIcon
                                    size={24}
                                    className={`${styles.collapseIcon} ${
                                        worksheetsCollapsed
                                            ? styles.collapseIconCollapsed
                                            : styles.collapseIconExpanded
                                    }`}
                                />
                                <ScoopText className={styles.sectionHeaderTitleText}>
                                    Worksheet metrics
                                </ScoopText>
                            </ScoopRow>
                        </ScoopRow>
                        <div className={styles.worksheetMetricsContainer}>
                            {worksheetMetrics.map((col, i) => {
                                const selected = config.selectedItems.some(
                                    (it) => it.measureName === col.columnName
                                )
                                return (
                                    <WorksheetMetric
                                        chartState={chartState}
                                        setConfig={setConfig}
                                        config={config}
                                        key={col.columnName + '-' + i}
                                        metricCol={col}
                                        handleWorksheetMetricSelect={handleWorksheetMetricSelect}
                                        selected={selected}
                                    />
                                )
                            })}
                        </div>
                    </div>
                    <ScoopDivider />
                </>
            )}
            <div className={styles.worksheetsSection}>
                <ScoopRow className={styles.sectionHeader}>
                    <ScoopText className={styles.sectionHeaderTitleText}>Worksheets</ScoopText>
                </ScoopRow>
                {config.worksheetID ? (
                    <ScoopText className={styles.worksheetName}>
                        {config.selectedWorksheetName}
                    </ScoopText>
                ) : (
                    <ScoopButton
                        variant="grey"
                        className={styles.filterButton}
                        onClick={() =>
                            sheetSourceModal.show({
                                config,
                                setConfig,
                                chartState,
                                workspaceMetadata,
                                workspaceID,
                                setWorksheetMetrics,
                                server,
                            })
                        }
                    >
                        Create from worksheet
                    </ScoopButton>
                )}
            </div>
            <ScoopMenu
                open={itemOptionsMenuOpen}
                anchorEl={currentItemRef.current}
                onClose={() => setItemOptionsMenuOpen(false)}
            >
                <ScoopMenuItem onClick={editItem}>Edit</ScoopMenuItem>
                <ScoopMenuItem onClick={deleteItem}>Delete</ScoopMenuItem>
            </ScoopMenu>
            <ScoopMenu
                open={Boolean(kpiMenuAnchorEl)}
                anchorEl={kpiMenuAnchorEl}
                onClose={() => setKpiMenuAnchorEl(null)}
            >
                <ScoopMenuItem
                    onClick={() => {
                        handleCreateKpi(false)
                        setKpiMenuAnchorEl(null)
                    }}
                >
                    <ScoopText>Add a KPI aggregating Scoop table data</ScoopText>
                </ScoopMenuItem>
                <ScoopMenuItem
                    onClick={() => {
                        handleCreateKpi(true)
                        setKpiMenuAnchorEl(null)
                    }}
                >
                    <ScoopText>Add a KPI calculated using other KPIs</ScoopText>
                </ScoopMenuItem>
            </ScoopMenu>
            <ScoopMenu
                open={Boolean(dateMenuAnchorEl)}
                anchorEl={dateMenuAnchorEl}
                onClose={() => {
                    setDateMenuAnchorEl(null)
                    setDateMenuColumn(null)
                }}
            >
                {dateMenuColumn && (
                    <>
                        <ScoopMenuItem
                            onClick={() => {
                                setSelectedColumnDate(
                                    JSON.stringify({
                                        selectedDate: 'Snapshot Date',
                                        reportSeriesTableID:
                                            dateMenuColumn.tableData.reportSeriesTableID,
                                        columnName: dateMenuColumn.columnData.columnName,
                                    })
                                )
                                setDateMenuAnchorEl(null)
                                setDateMenuColumn(null)
                            }}
                        >
                            <ScoopRow className={styles.dateMenuItem}>
                                <ScoopText>by Snapshot Date</ScoopText>
                                {!config.selectedDates?.get(
                                    dateMenuColumn.tableData.reportSeriesTableID +
                                        ':' +
                                        dateMenuColumn.columnData.columnName
                                ) && <CheckIcon size={14} />}
                            </ScoopRow>
                        </ScoopMenuItem>
                        {dateMenuColumn.tableData.dates?.map((date) => {
                            const isDateSelected =
                                config.selectedDates?.get(
                                    dateMenuColumn.tableData.reportSeriesTableID +
                                        ':' +
                                        dateMenuColumn.columnData.columnName
                                ) === date
                            return (
                                <ScoopMenuItem
                                    key={date}
                                    onClick={() => {
                                        setSelectedColumnDate(
                                            JSON.stringify({
                                                selectedDate: date,
                                                reportSeriesTableID:
                                                    dateMenuColumn.tableData.reportSeriesTableID,
                                                columnName: dateMenuColumn.columnData.columnName,
                                            })
                                        )
                                        setDateMenuAnchorEl(null)
                                        setDateMenuColumn(null)
                                    }}
                                >
                                    <ScoopRow className={styles.dateMenuItem}>
                                        <ScoopText>by {date}</ScoopText>
                                        {isDateSelected && <CheckIcon size={14} />}
                                    </ScoopRow>
                                </ScoopMenuItem>
                            )
                        })}
                    </>
                )}
            </ScoopMenu>
            <ScoopMenu
                open={Boolean(kpiOptionsMenuAnchorEl)}
                anchorEl={kpiOptionsMenuAnchorEl}
                onClose={() => {
                    setKpiOptionsMenuAnchorEl(null)
                    setKpiOptionsMenuKpi(null)
                }}
            >
                <ScoopMenuItem onClick={() => handleEditKpi(kpiOptionsMenuKpi)}>
                    <ScoopRow className={styles.kpiMenuItem}>
                        <PencilSimpleIcon size={16} />
                        <ScoopText>Edit</ScoopText>
                    </ScoopRow>
                </ScoopMenuItem>
                <ScoopMenuItem onClick={() => handleCloneKpi(kpiOptionsMenuKpi)}>
                    <ScoopRow className={styles.kpiMenuItem}>
                        <CopyIcon size={16} />
                        <ScoopText>Clone</ScoopText>
                    </ScoopRow>
                </ScoopMenuItem>
                <ScoopMenuItem onClick={() => handleDeleteKpi(kpiOptionsMenuKpi)}>
                    <ScoopRow className={styles.kpiMenuItemDelete}>
                        <TrashIcon size={16} />
                        <ScoopText>Delete</ScoopText>
                    </ScoopRow>
                </ScoopMenuItem>
            </ScoopMenu>
        </>
    )
}
